<template>
  <div class="mt-4">
    <!-- 에러 배너 (디버그) -->
    <v-alert v-if="lastError" dense type="error" outlined dismissible class="mx-auto mb-2" max-width="95%"
      @input="lastError = ''">
      <div class="d-flex">
        <div class="flex-grow-1">
          <strong>API 호출 실패:</strong>
          <pre class="hb-err mb-1">{{ lastError }}</pre>
          <div class="text-caption">
            점검: ① CMS_Back 에서 <code>DT_API_URL</code> / <code>DT_ADMIN_SECRET</code> 환경변수<br/>
            ② Service_DT 서버 기동 + <code>DT_ADMIN_SECRET</code> 일치 여부<br/>
            ③ 마이그레이션 적용: <code>20260520_1~4_*.sql</code> (오늘 요약 자체는 bets 직조회라 통계 4테이블 없어도 동작해야 함)
          </div>
        </div>
      </div>
    </v-alert>

    <!-- 헤더 -->
    <v-card class="mx-auto" max-width="95%" elevation="2">
      <v-toolbar dark dense color="indigo darken-2">
        <v-toolbar-title>
          <v-icon left small>mdi-flash</v-icon>오늘 실시간 ({{ statDate || '-' }} KST)
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-chip x-small outlined color="white">
          <v-icon left x-small>mdi-refresh</v-icon>10s 갱신
        </v-chip>
        <v-btn icon small dark @click="loadAll" class="ml-1" :loading="loading">
          <v-icon small>mdi-reload</v-icon>
        </v-btn>
      </v-toolbar>
      <v-card-text class="pa-3">
        <v-row dense>
          <v-col cols="6" md="3" v-for="(c, i) in summaryCards" :key="i">
            <v-card flat :color="c.bg" class="pa-3">
              <div class="text-caption grey--text text--darken-2">{{ c.label }}</div>
              <div class="text-h6 font-weight-bold">{{ c.value }}</div>
            </v-card>
          </v-col>
        </v-row>
        <div class="text-caption grey--text mt-2">캐시: {{ cachedAt || '-' }}</div>
      </v-card-text>
    </v-card>

    <!-- 매체별 -->
    <v-card class="mx-auto mt-6" max-width="95%" elevation="2">
      <v-toolbar dark dense color="teal darken-2">
        <v-toolbar-title>오늘 매체별 (베팅금액 순)</v-toolbar-title>
        <v-spacer></v-spacer>
        <span class="text-caption white--text">상위 {{ byMediaRows.length }}건</span>
      </v-toolbar>
      <v-data-table dense :headers="mediaHeaders" :items="byMediaRows" :items-per-page="20"
        item-key="app_key" :loading="loading"
        no-data-text="오늘 데이터가 없습니다.">
        <template v-slot:item.total_bet_amount="{ item }">{{ fmt(item.total_bet_amount) }}</template>
        <template v-slot:item.total_payout_amount="{ item }">{{ fmt(item.total_payout_amount) }}</template>
        <template v-slot:item.net_revenue="{ item }">
          <span :class="item.net_revenue >= 0 ? 'green--text' : 'red--text'">{{ fmt(item.net_revenue) }}</span>
        </template>
      </v-data-table>
    </v-card>

    <!-- 게임별 -->
    <v-card class="mx-auto mt-6" max-width="95%" elevation="2">
      <v-toolbar dark dense color="deep-orange darken-2">
        <v-toolbar-title>오늘 게임별 (베팅횟수 순)</v-toolbar-title>
        <v-spacer></v-spacer>
        <span class="text-caption white--text">상위 {{ byGameRows.length }}건</span>
      </v-toolbar>
      <v-data-table dense :headers="gameHeaders" :items="byGameRows" :items-per-page="20"
        item-key="game_id" :loading="loading"
        no-data-text="오늘 데이터가 없습니다.">
        <template v-slot:item.margin="{ item }">
          <v-chip x-small outlined :color="marginChipColor(item.margin)">
            {{ pct(item.margin) }}
          </v-chip>
        </template>
        <template v-slot:item.total_bet_amount="{ item }">{{ fmt(item.total_bet_amount) }}</template>
        <template v-slot:item.total_payout="{ item }">{{ fmt(item.total_payout) }}</template>
        <template v-slot:item.refund_bet_count="{ item }">
          <span v-if="item.refund_bet_count > 0" :title="`환불금액 ${fmt(item.refund_bet_amount)}`">
            <v-chip x-small color="orange" dark>{{ item.refund_bet_count }}</v-chip>
          </span>
          <span v-else class="grey--text">-</span>
        </template>
        <template v-slot:item.unsettled_bet_count="{ item }">
          <v-chip v-if="item.unsettled_bet_count > 0" x-small color="grey" dark>{{ item.unsettled_bet_count }}</v-chip>
          <span v-else class="grey--text">-</span>
        </template>
        <template v-slot:item.effective_margin="{ item }">
          <span v-if="item.effective_margin == null" class="grey--text" :title="'정산된 베팅 0건 — 모두 환불/미정산'">-</span>
          <v-chip v-else x-small :color="effectiveMarginColor(item.effective_margin, item.margin)" dark>
            {{ pct(item.effective_margin) }}
          </v-chip>
        </template>
      </v-data-table>
    </v-card>

    <div class="py-12"></div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
export default {
  name: 'Danta_Stats_Today',
  data: () => ({
    loading: false,
    lastError: '',
    statDate: '',
    cachedAt: '',
    summary: {},
    byMediaRows: [],
    byGameRows: [],
    _timer: null,
    mediaHeaders: [
      { text: '매체ID', value: 'media_id' },
      { text: 'app_key', value: 'app_key' },
      { text: 'DAU',   value: 'active_user_count', align: 'right' },
      { text: '베팅수', value: 'bet_count',         align: 'right' },
      { text: '베팅금', value: 'total_bet_amount',  align: 'right' },
      { text: '지급액', value: 'total_payout_amount', align: 'right' },
      { text: '순수익', value: 'net_revenue', align: 'right' },
    ],
    gameHeaders: [
      { text: '게임ID', value: 'game_id', align: 'right' },
      { text: '게임명', value: 'game_title' },
      { text: '템플릿', value: 'template_code' },
      { text: '설정', value: 'margin', align: 'right' },             // games.margin (5% 등)
      { text: '참여자', value: 'participant_count', align: 'right' },
      { text: '라운드', value: 'round_count', align: 'right' },
      { text: '베팅수', value: 'bet_count', align: 'right' },
      { text: '환불', value: 'refund_bet_count', align: 'right' },   // 환불(draw) 베팅 수
      { text: '미정산', value: 'unsettled_bet_count', align: 'right' },
      { text: '베팅금', value: 'total_bet_amount', align: 'right' },
      { text: '지급액', value: 'total_payout', align: 'right' },
      { text: '실효마진', value: 'effective_margin', align: 'right' }, // 환불 제외 실제 마진율
    ],
  }),
  computed: {
    summaryCards() {
      const s = this.summary || {};
      return [
        { label: '베팅 횟수',  value: this.fmt(s.bet_count),         bg: '#E8F5E9' },
        { label: 'DAU',         value: this.fmt(s.active_user_count), bg: '#E3F2FD' },
        { label: '베팅 금액',  value: this.fmt(s.total_bet_amount),  bg: '#FFF3E0' },
        { label: '지급액',      value: this.fmt(s.total_payout_amount), bg: '#FCE4EC' },
        { label: '순수익',      value: this.fmt(s.net_revenue),       bg: '#F3E5F5' },
        // 환불 제외 실효 마진율 — 운영자 실수익률. 환불 비중 큰 날엔 0% 처럼 보여서 핵심 지표
        { label: '실효 마진율', value: this.pct(s.effective_margin),   bg: '#E1F5FE' },
        { label: '참여 게임',  value: this.fmt(s.game_count),        bg: '#E0F7FA' },
        { label: '라운드',      value: this.fmt(s.round_count),       bg: '#EDE7F6' },
        { label: '환불(draw)', value: this.fmt(s.draw_count),        bg: '#FFF8E1' },
        { label: '취소 베팅',  value: this.fmt(s.cancelled_count),   bg: '#FFEBEE' },
      ];
    },
  },
  created() {
    this.loadAll();
    this._timer = setInterval(() => this.loadAll(true), 10000);
  },
  beforeDestroy() {
    if (this._timer) clearInterval(this._timer);
  },
  methods: {
    ...mapActions([
      'DANTA_STATS_TODAY_SUMMARY',
      'DANTA_STATS_TODAY_BY_MEDIA',
      'DANTA_STATS_TODAY_BY_GAME',
    ]),
    fmt(n) {
      if (n == null) return '-';
      return Number(n).toLocaleString();
    },
    pct(rate) {
      if (rate == null) return '-';
      return `${(Number(rate) * 100).toFixed(1)}%`;
    },
    // games.margin (설정값) 표시 색
    marginChipColor(m) {
      if (m == null || m === 0) return 'red';     // 설정 자체가 0/null = 운영 의도 다름
      if (m < 0.03)              return 'orange'; // 3% 미만은 매우 낮음
      return 'indigo';
    },
    // 실효 마진 vs 설정 마진 비교 — 큰 괴리는 운영자 주의 신호
    effectiveMarginColor(eff, setM) {
      if (eff == null) return 'grey';
      if (eff < 0)      return 'red';   // 음수 = 운영자 손해
      if (setM != null && Math.abs(eff - setM) > 0.02) return 'amber';  // 설정과 2%p 이상 차이
      return 'green';
    },
    async loadAll(silent) {
      if (!silent) this.loading = true;
      try {
        const [s, m, g] = await Promise.all([
          this.DANTA_STATS_TODAY_SUMMARY(),
          this.DANTA_STATS_TODAY_BY_MEDIA({ limit: 50 }),
          this.DANTA_STATS_TODAY_BY_GAME({ limit: 50 }),
        ]);
        this.summary  = s || {};
        this.statDate = (s && s.stat_date) || '';
        this.cachedAt = (s && s.cached_at) || '';
        this.byMediaRows = (m && m.rows) || [];
        this.byGameRows  = (g && g.rows) || [];
        this.lastError = '';
      } catch (err) {
        const msg = (err && (err.message || err.err_message)) || String(err);
        this.lastError = msg;
        console.error('[Stats_Today] load 실패:', err);
      } finally {
        if (!silent) this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.hb-err {
  background: rgba(255,255,255,0.6);
  padding: 4px 6px;
  font-size: 11px;
  white-space: pre-wrap;
  margin: 4px 0;
  border-radius: 3px;
  max-height: 100px;
  overflow: auto;
}
</style>
